import type { ReactNode } from "react";

export interface CategoryFilters {
  type: string;
  region: string;
  year: string;
  tags: string[];
  sort: string;
}

interface FilterBarProps {
  options: {
    types: string[];
    regions: string[];
    years: string[];
    tags: string[];
  };
  value: CategoryFilters;
  onChange: (value: CategoryFilters) => void;
  onReset: () => void;
}

const sortOptions = [
  { value: "latest", label: "最新更新" },
  { value: "hot", label: "最热播放" },
  { value: "rating", label: "评分最高" },
];

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterBar({ options, value, onChange, onReset }: FilterBarProps) {
  const toggleTag = (tag: string) => {
    const has = value.tags.includes(tag);
    onChange({
      ...value,
      tags: has ? value.tags.filter((t) => t !== tag) : [...value.tags, tag],
    });
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="space-y-3.5">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span className="text-sm font-medium text-foreground-500 md:w-14 shrink-0">类型</span>
          <div className="flex flex-wrap gap-2">
            {options.types.map((t) => (
              <Pill key={t} active={value.type === t} onClick={() => onChange({ ...value, type: t })}>
                {t}
              </Pill>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span className="text-sm font-medium text-foreground-500 md:w-14 shrink-0">地区</span>
          <div className="flex flex-wrap gap-2">
            {options.regions.map((r) => (
              <Pill key={r} active={value.region === r} onClick={() => onChange({ ...value, region: r })}>
                {r}
              </Pill>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span className="text-sm font-medium text-foreground-500 md:w-14 shrink-0">年份</span>
          <div className="flex flex-wrap gap-2">
            {options.years.map((y) => (
              <Pill key={y} active={value.year === y} onClick={() => onChange({ ...value, year: y })}>
                {y}
              </Pill>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-2 md:gap-4">
          <span className="text-sm font-medium text-foreground-500 md:w-14 shrink-0 md:pt-1.5">标签</span>
          <div className="flex flex-wrap gap-2">
            {options.tags.map((t) => (
              <Pill key={t} active={value.tags.includes(t)} onClick={() => toggleTag(t)}>
                {t}
              </Pill>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 pt-3.5 border-t border-background-200">
          <span className="text-sm font-medium text-foreground-500 md:w-14 shrink-0">排序</span>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((s) => (
              <Pill key={s.value} active={value.sort === s.value} onClick={() => onChange({ ...value, sort: s.value })}>
                {s.label}
              </Pill>
            ))}
          </div>
          <button
            type="button"
            onClick={onReset}
            className="md:ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-200 text-sm text-foreground-600 hover:border-secondary-400 hover:text-secondary-600 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}